import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import {
  enqueueTask,
  restartableTask,
  dropTask,
  task,
} from 'ember-concurrency';
import { getObservable, queryManager } from 'ember-apollo-client';
import { v4 as uuid } from 'uuid';
import { variation } from 'ember-launch-darkly';
import sendMessageMutation from 'my-phorest/gql/mutations/send-chat-message.graphql';
import updateChatMessagesSeenStatusMutation from 'my-phorest/gql/mutations/update-chat-messages-seen-status.graphql';
import enableBranchChatAiMutation from 'my-phorest/gql/mutations/enable-branch-chat-ai.graphql';
import disableBranchChatAiMutation from 'my-phorest/gql/mutations/disable-branch-chat-ai.graphql';

import getChatMessagesQuery from 'my-phorest/gql/queries/chat-messages.graphql';
import getChatConversationsQuery from 'my-phorest/gql/queries/chat-conversations.graphql';
import getUnreadMessagesCountQuery from 'my-phorest/gql/queries/chat-unread-messages-count.graphql';
import getWhatsappTemplatesQuery from 'my-phorest/gql/queries/whatsapp-templates.graphql';
import getBranchChatAiSettingsQuery from 'my-phorest/gql/queries/branch-chat-ai-settings.graphql';

export default class TwoWayChatService extends Service {
  @queryManager apollo;
  @service session;
  @service apolloSubscriptions;
  @service eventPropagator;
  @service access;
  @service pendo;
  @service whatsapp;
  @service intl;
  @service notifications;

  @tracked screen = '';
  @tracked selectedClient = null;
  @tracked selectedChat = null;
  @tracked selectedConversation = null;
  @tracked selectedTab = 'all-chats';
  @tracked showUnreadConversationsOnly = false;
  @tracked selectedTemplate = null;
  @tracked branchAiSettings = null;

  getChatMessagesQuery = getChatMessagesQuery;

  constructor() {
    super(...arguments);
    this.eventPropagator.subscribe(
      'clientChatChange',
      this.refetchResourcesTask.perform
    );
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.eventPropagator.unsubscribe(
      'clientChatChange',
      this.refetchResourcesTask.perform
    );
  }

  get chatConversations() {
    return this.getChatConversationsTask.lastSuccessful?.value.chatConversations?.edges.map(
      (edge) => edge.node
    );
  }

  get chatConversationsChannels() {
    const channels = [];

    if (this.isTwoWayEnabled) {
      const smsPhoneNumber =
        this.session.branch.twoWayMessagingPhoneNumber ??
        this.session.branch.marketing.branchSmsNumber;
      channels.push({
        label: 'SMS',
        phoneNumber: smsPhoneNumber,
        name: 'SMS',
      });
    }

    if (
      variation('release-marketing-whatsapp-integration-p-10') &&
      this.whatsapp.hasSuccessfulConnection
    ) {
      const whatsappPhoneNumber =
        this.whatsapp.successfulConnection.displayPhoneNumber;
      channels.push({
        label: 'WhatsApp',
        phoneNumber: whatsappPhoneNumber,
        name: 'WHATSAPP',
      });
    }
    return channels;
  }

  get unreadMessagesCount() {
    const count =
      this.getUnreadMessagesCountTask.lastSuccessful?.value
        .chatUnreadMessageCount.count;
    return count > 99 ? '99+' : count;
  }

  get isWindowChatOpen() {
    return !!this.screen;
  }

  get hasSelectedClientPhoneNumber() {
    return !!this.selectedClient?.mobile;
  }

  get queryVariables() {
    return {
      clientId: this.selectedClient.id,
    };
  }

  get whatsappTemplates() {
    return this.getWhatsappTemplatesTask.lastSuccessful?.value.edges.map(
      (edge) => edge.node
    );
  }

  get isTwoWayEnabled() {
    return (
      this.session.branch.isTwoWayMessagingEnabled &&
      this.access.hasPermission('two-way-chat')
    );
  }

  @action
  toggleWindowChat() {
    if (this.isWindowChatOpen) {
      this.closeWindowChatTask.perform();
    } else {
      this.openWindowChat();
    }
  }

  @action
  openWindowChat() {
    this.setScreenTask.perform('chats-list');
    // Track the usage of two-way chat feature
    this.pendo.trackEvent('Two-Way Chat - Feature Usage');
  }

  @action
  reset() {
    this.selectedClient = null;
    this.selectedChat = null;
    this.selectedConversation = null;
    this.selectedTab = 'all-chats';
    this.showUnreadConversationsOnly = false;
    this.selectedTemplate = null;
  }

  @action
  toggleShowUnreadConversationsOnly() {
    this.showUnreadConversationsOnly = !this.showUnreadConversationsOnly;
    this.getChatConversationsTask.perform();
  }

  @action
  selectClient(client, conversation = null) {
    this.selectedClient = client;
    this.selectedConversation = conversation;
  }

  @action
  setNextScreen(nextScreen) {
    this.setScreenTask.perform(nextScreen);
  }

  subscribe() {
    const hasTwoWayPermission = this.access.hasPermission('two-way-chat');
    const isWhatsappEnabled =
      variation('release-marketing-whatsapp-integration-p-10') &&
      hasTwoWayPermission; // A check will be included in branch settings for the milestone 2

    if (this.isTwoWayEnabled || isWhatsappEnabled) {
      this.getUnreadMessagesCountTask.perform();
      this.apolloSubscriptions.subscribe('chatChange', {
        accountId: this.session.accountId,
      });
    }

    if (isWhatsappEnabled) {
      this.whatsapp.getWhatsAppConnectionsTask.perform();
    }
  }

  @restartableTask
  *getChatConversationsTask() {
    return yield this.apollo.query({
      query: getChatConversationsQuery,
      variables: {
        unread: this.showUnreadConversationsOnly ? true : undefined,
        staffMemberId:
          this.selectedTab === 'my-chats'
            ? this.session.currentStaffMemberId
            : undefined,
        channels: this.chatConversationsChannels.map((channel) => channel.name),
      },
      fetchPolicy: 'network-only',
    });
  }

  @restartableTask
  *getWhatsappTemplatesTask() {
    return yield this.apollo.query(
      {
        query: getWhatsappTemplatesQuery,
        variables: {
          branchId: this.session.branchId,
        },
      },
      'whatsappTemplates'
    );
  }

  @restartableTask
  *refetchResourcesTask({ resource }) {
    let clientId;
    if (resource?.clientId) {
      clientId = resource.clientId;
    }

    yield this.getUnreadMessagesCountTask.perform();
    if (this.screen === 'chats-list') {
      yield this.getChatConversationsTask.perform();
    }
    if (this.screen === 'client-chat' && clientId === this.selectedClient.id) {
      yield this.observable.refetch();
    }
  }

  @restartableTask
  *setScreenTask(screen) {
    const previousScreen = this.screen;
    this.screen = screen;
    if (previousScreen === 'client-chat') {
      yield this.markConversationAsReadTask.perform();
      yield this.getUnreadMessagesCountTask.perform();
    }
    if (this.screen === 'chats-list') {
      yield this.getChatConversationsTask.perform();
    }
    if (this.screen === 'templates-list') {
      yield this.getWhatsappTemplatesTask.perform();
    }
  }

  @enqueueTask
  *closeWindowChatTask() {
    yield this.setScreenTask.perform('');
    this.reset();
  }

  @restartableTask
  *getChatMessagesTask(variables = {}) {
    const queryResult = yield this.apollo.watchQuery(
      {
        query: getChatMessagesQuery,
        variables,
        fetchPolicy: 'network-only',
      },
      'chatMessages'
    );
    this.observable = getObservable(queryResult);
    return queryResult;
  }

  @task
  *sendMessageTask({
    text,
    fromMobile,
    templateId = null,
    operationName = 'sendMessage',
    channel = 'SMS',
  }) {
    const { id: clientId, rawMobile: toMobile } = this.selectedClient;

    const variables = {
      accountId: this.session.accountId,
      clientId,
      fromMobile: fromMobile.toString(),
      isMessageSeen: true,
      messageUuid: uuid(),
      staffFirstName: this.session.currentStaffMember?.firstName,
      staffLastName: this.session.currentStaffMember?.lastName,
      staffMemberId: this.session.currentStaffMemberId,
      toMobile,
      channel,
    };

    if (templateId) {
      variables.templateId = templateId;
    }
    if (text) {
      variables.text = text;
    }

    yield this.apollo.mutate(
      {
        mutation: sendMessageMutation,
        variables,
      },
      operationName
    );
  }

  @enqueueTask
  *sendUserInputTask(message) {
    const fromMobile = this.session.branch.twoWayMessagingPhoneNumber
      ? this.session.branch.twoWayMessagingPhoneNumber
      : this.session.branch.marketing.branchSmsNumber;

    yield this.sendMessageTask.perform({
      text: message,
      fromMobile,
    });

    // Track outbound message count
    this.pendo.trackEvent('Two-Way Chat - Outbound Message');

    // This is a workaround to ensure the chat messages are updated after the message is sent beacuse we don't receive an event from the backend
    setTimeout(() => {
      this.observable.refetch();
    }, 2000);
  }

  @dropTask
  *sendTemplateTask() {
    yield this.sendMessageTask.perform({
      fromMobile: this.whatsapp.successfulConnection.displayPhoneNumber,
      templateId: this.selectedTemplate.id,
      channel: 'WHATSAPP',
      operationName: 'sendTemplate',
    });
  }
  @restartableTask
  *getUnreadMessagesCountTask() {
    return yield this.apollo.query({
      query: getUnreadMessagesCountQuery,
      variables: {
        accountId: this.session.accountId,
      },
      fetchPolicy: 'no-cache',
    });
  }

  @enqueueTask
  *markConversationAsReadTask() {
    const { id: clientId } = this.selectedClient;
    yield this.apollo.mutate(
      {
        mutation: updateChatMessagesSeenStatusMutation,
        variables: {
          clientId,
          messageUuids: [],
          seenStatus: true,
        },
      },
      'updateChatMessagesSeenStatus'
    );
  }

  @enqueueTask
  *markConversationAsUnreadTask(clientId, messageUuid) {
    yield this.apollo.mutate(
      {
        mutation: updateChatMessagesSeenStatusMutation,
        variables: {
          clientId,
          messageUuids: [messageUuid],
          seenStatus: false,
        },
      },
      'updateChatMessagesSeenStatus'
    );
    yield this.getChatConversationsTask.perform();
    yield this.getUnreadMessagesCountTask.perform();
  }

  @restartableTask
  *getBranchAiSettingsTask() {
    const result = yield this.apollo.query(
      {
        query: getBranchChatAiSettingsQuery,
        variables: {
          branchId: this.session.branchId,
        },
        fetchPolicy: 'network-only',
      },
      'branchChatAiSettings'
    );
    this.branchAiSettings = result;
    return result;
  }

  @action
  toggleBranchAi() {
    if (this.branchAiSettings?.isAiEnabled) {
      this.disableBranchAiTask.perform();
    } else {
      this.enableBranchAiTask.perform();
    }
  }

  @enqueueTask
  *enableBranchAiTask() {
    try {
      const result = yield this.apollo.mutate(
        {
          mutation: enableBranchChatAiMutation,
          variables: {
            branchId: this.session.branchId,
          },
        },
        'enableBranchChatAi'
      );
      this.branchAiSettings = result;
      this.notifications.success(
        this.intl.t('two-way-chat.ai-enabled-successfully')
      );
    } catch (error) {
      this.notifications.failure(this.intl.t('two-way-chat.ai-enable-failed'));
    }
  }

  @enqueueTask
  *disableBranchAiTask() {
    try {
      const result = yield this.apollo.mutate(
        {
          mutation: disableBranchChatAiMutation,
          variables: {
            branchId: this.session.branchId,
          },
        },
        'disableBranchChatAi'
      );
      this.branchAiSettings = result;
      this.notifications.success(
        this.intl.t('two-way-chat.ai-disabled-successfully')
      );
    } catch (error) {
      this.notifications.failure(this.intl.t('two-way-chat.ai-disable-failed'));
    }
  }
}
